import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchProductTables,
  createProductTable as apiCreateProductTable,
  fetchProductTableRows,
  replaceProductTableRows,
  fetchProductTableRowsTier1,
  replaceProductTableRowsTier1,
} from "../api/productTablesApi";

const emptyList = [];

export function useProductTables(token, onUnauthorized) {
  const [tables, setTables] = useState(emptyList);
  const [rowsByTable, setRowsByTable] = useState({});

  const handleAuthError = useCallback(
    async (error) => {
      if (error?.status === 401 && typeof onUnauthorized === "function") {
        await onUnauthorized();
      }
      throw error;
    },
    [onUnauthorized]
  );

  const refreshTables = useCallback(async () => {
    if (!token) {
      setTables(emptyList);
      setRowsByTable({});
      return emptyList;
    }

    try {
      const list = await fetchProductTables(token);
      setTables(list);
      return list;
    } catch (error) {
      await handleAuthError(error);
    }
  }, [token, handleAuthError]);

  const loadRows = useCallback(
    async (tableId, options = {}) => {
      if (!token || !tableId) return emptyList;

      const { forceRefresh = false } = options;
      if (!forceRefresh && rowsByTable[tableId]) {
        return rowsByTable[tableId];
      }

      try {
        const rows = await fetchProductTableRows(token, tableId);
        setRowsByTable((prev) => ({ ...prev, [tableId]: rows }));
        return rows;
      } catch (error) {
        await handleAuthError(error);
      }
    },
    [token, handleAuthError, rowsByTable]
  );

  const loadRowsTier1 = useCallback(
    async (tableId, options = {}) => {
      if (!token || !tableId) return emptyList;

      const { forceRefresh = false } = options;
      if (!forceRefresh && rowsByTable[tableId]) {
        return rowsByTable[tableId];
      }

      try {
        const rows = await fetchProductTableRowsTier1(token, tableId);
        setRowsByTable((prev) => ({ ...prev, [tableId]: rows }));
        return rows;
      } catch (error) {
        await handleAuthError(error);
      }
    },
    [token, handleAuthError, rowsByTable]
  );

  const saveRows = useCallback(
    async (tableId, records) => {
      if (!token || !tableId) return emptyList;

      try {
        const rows = await replaceProductTableRows(token, tableId, records);
        setRowsByTable((prev) => ({ ...prev, [tableId]: rows }));
        return rows;
      } catch (error) {
        await handleAuthError(error);
      }
    },
    [token, handleAuthError]
  );

  const saveRowsTier1 = useCallback(
    async (tableId, records) => {
      if (!token || !tableId) return emptyList;

      try {
        const rows = await replaceProductTableRowsTier1(token, tableId, records);
        setRowsByTable((prev) => ({ ...prev, [tableId]: rows }));
        return rows;
      } catch (error) {
        await handleAuthError(error);
      }
    },
    [token, handleAuthError]
  );

  const createTable = useCallback(
    async (payload) => {
      if (!token) return null;

      try {
        const created = await apiCreateProductTable(token, payload);
        await refreshTables();
        return created;
      } catch (error) {
        await handleAuthError(error);
      }
    },
    [token, refreshTables, handleAuthError]
  );

  const clearRowsCache = useCallback(() => {
    setRowsByTable({});
  }, []);

  useEffect(() => {
    if (!token) {
      setTables(emptyList);
      setRowsByTable({});
    }
  }, [token]);

  return useMemo(
    () => ({
      tables,
      rowsByTable,
      refreshTables,
      loadRows,
      loadRowsTier1,
      saveRows,
      saveRowsTier1,
      createTable,
      clearRowsCache,
    }),
    [
      tables,
      rowsByTable,
      refreshTables,
      loadRows,
      loadRowsTier1,
      saveRows,
      saveRowsTier1,
      createTable,
      clearRowsCache,
    ]
  );
}
